import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Plus, X, Edit2, Trash2 } from "lucide-react";
import usePageTitle from "@/hooks/use-page-title";

const ROLES = [
  "admin",
  "office_staff",
  "sales_executive",
  "designer",
  "production_manager",
  "machine_operator",
  "accountant",
  "delivery_staff",
];

const EMPTY = { email: "", password: "", name: "", role: "office_staff", phone: "" };

export default function Staff() {
  usePageTitle("Staff");
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [err, setErr] = useState("");

  const load = () =>
    api
      .get("/auth/users")
      .then((r) => setUsers(r.data.filter((u) => u.role !== "customer")));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setErr("");
    setShowForm(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, password: "", name: u.name, role: u.role, phone: u.phone || "" });
    setErr("");
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, phone: form.phone };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
      } else {
        await api.post("/users", form);
      }
      setShowForm(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      load();
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="staff-root">
      <div className="flex items-end justify-between border-b border-[hsl(var(--border))] pb-6">
        <div>
          <div className="overline mb-2">Team</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">
            Staff & Roles
          </h1>
        </div>
        <button onClick={openNew} className="btn-swiss btn-accent" data-testid="new-staff-btn">
          <Plus size={14} /> Add Staff
        </button>
      </div>

      <div className="card-swiss overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[hsl(var(--border))]">
              <th className="px-4 py-3 text-left overline">Name</th>
              <th className="px-4 py-3 text-left overline">Email</th>
              <th className="px-4 py-3 text-left overline">Phone</th>
              <th className="px-4 py-3 text-left overline">Role</th>
              <th className="px-4 py-3 text-right overline">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]" data-testid={`staff-row-${u.id}`}>
                <td className="px-4 py-3 font-semibold">{u.name}</td>
                <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                <td className="px-4 py-3 font-mono text-xs">{u.phone || "—"}</td>
                <td className="px-4 py-3">
                  <span className="text-xs font-semibold uppercase tracking-wider border border-[hsl(var(--border))] px-2 py-1">
                    {u.role.replace(/_/g, " ")}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => openEdit(u)}
                    className="p-1.5 hover:text-[#FF6B00]"
                    data-testid={`edit-staff-${u.id}`}
                    title="Edit"
                  >
                    <Edit2 size={14} />
                  </button>
                  {u.role !== "super_admin" && (
                    <button
                      onClick={() => remove(u)}
                      className="p-1.5 hover:text-[#FF6B00]"
                      data-testid={`delete-staff-${u.id}`}
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">
                  No staff yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="staff-modal">
          <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] max-w-lg w-full">
            <div className="flex items-center justify-between p-5 border-b border-[hsl(var(--border))]">
              <div className="font-display text-xl font-bold">
                {editing ? "Edit Staff" : "New Staff"}
              </div>
              <button onClick={() => setShowForm(false)} data-testid="close-staff-modal">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4">
              <div>
                <label className="overline block mb-2">Name *</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]"
                  data-testid="staff-input-name"
                />
              </div>
              <div>
                <label className="overline block mb-2">Email {editing ? "" : "*"}</label>
                <input
                  type="email"
                  disabled={!!editing}
                  required={!editing}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00] disabled:opacity-60"
                  data-testid="staff-input-email"
                />
              </div>
              <div>
                <label className="overline block mb-2">Phone</label>
                <input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]"
                  data-testid="staff-input-phone"
                />
              </div>
              <div>
                <label className="overline block mb-2">Role *</label>
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]"
                  data-testid="staff-input-role"
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="overline block mb-2">
                  Password {editing ? "(leave blank to keep)" : "*"}
                </label>
                <input
                  type="password"
                  required={!editing}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm outline-none focus:border-[#FF6B00]"
                  data-testid="staff-input-password"
                />
              </div>
              {err && (
                <div className="text-sm text-red-600" data-testid="staff-form-error">
                  {err}
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="btn-swiss btn-outline">
                  Cancel
                </button>
                <button type="submit" className="btn-swiss btn-accent" data-testid="save-staff-btn">
                  {editing ? "Save Changes" : "Create User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
